import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { createEmptyRow } from "../../components/reportValue/InherentRiskTab";
import { createEmptyControlRow } from "../../components/reportValue/ControlEfficacyTab";
import { calculateSectionRiskScore } from "@/services/sectionRiskService";
import ReportValuesLayout from "@/components/reportValue/ReportValuesLayout";
import {
    createLossRow,
    createInitialRecommendationRows,
    getUniqueSectionNamesFromSectionRiskRows,
    mapServerLossRowsToState,
} from "@/components/reportValue/LossAndRecommendationsTab";
import api from "@/lib/api";

const generateRowId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID()
    }
    return `row-${Date.now()}-${Math.random().toString(16).slice(2)}`
}


const EditValuesPage = () => {
    const [activeTab, setActiveTab] = useState("report-summary")
    const { id: reportId } = useParams()
    const [isSaving, setIsSaving] = useState(false)
    const [isLoadingValues, setIsLoadingValues] = useState(false)
    const [reportName, setReportName] = useState("")

    const [riskRows, setRiskRows] = useState([])
    const [controlRows, setControlRows] = useState([])
    const [sectionRows, setSectionRows] = useState([])
    const [lossRows, setLossRows] = useState([createLossRow()])
    const [recommendationRows, setRecommendationRows] = useState([])
    const [deletedLossIds, setDeletedLossIds] = useState([])


    useEffect(() => {
        setSectionRows(() =>
            riskRows.map((riskRow) => {
                const controlRow = controlRows.find((row) => row.id === riskRow.id)
                const controlEfficacy = controlRow?.controlEfficacy ?? ""
                const inherentRiskScore = riskRow.score ?? ""
                const inherentRiskLabel = riskRow.inherentRisk ?? ""
                const persistedId = riskRow.persistedId ?? controlRow?.persistedId ?? null

                return {
                    id: riskRow.id,
                    persistedId,
                    risk: riskRow.risk,
                    category: riskRow.category,
                    inherentRiskLabel,
                    inherentRiskScore,
                    controlEfficacy,
                    riskScore: calculateSectionRiskScore(controlEfficacy, inherentRiskScore),
                }
            })
        )
    }, [riskRows, controlRows])

    useEffect(() => {
        const fetchReportValues = async () => {
            if (!reportId) return
            setIsLoadingValues(true)
            try {
                // First, get the report to find the site and mine type
                const reportResponse = await api.get(`/reports/${reportId}`)
                const reportData = reportResponse.data?.data || reportResponse.data
                const siteId = reportData?.site_id

                if (!siteId) {
                    toast.error("Report site not found")
                    setIsLoadingValues(false)
                    return
                }

                // Get site to find mine type
                const siteResponse = await api.get(`/sites/${siteId}`)
                const siteData = siteResponse.data?.data || siteResponse.data
                const mineTypeId = siteData?.mine_id

                if (!mineTypeId) {
                    toast.error("Mine type not found for this site")
                    setIsLoadingValues(false)
                    return
                }

                // Get categories from mine type, ordered by the 'order' field
                const categoriesResponse = await api.get(`/mine-type/${mineTypeId}/category`)
                const categories = categoriesResponse.data?.data || []

                // Sort categories by order field
                const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0))

                // Get existing report values
                const response = await api.get(`/reports/${reportId}/values`)
                const serverRows = response.data?.data ?? []
                const lossServerRows = response.data?.loss ?? []
                const recommendationServerRows = response.data?.recommendations ?? []

                // Create rows from categories, merging with existing data
                const categoryRows = sortedCategories.map((category, index) => {
                    const existingRow = serverRows.find(
                        (row) =>
                            row.risk?.trim().toLowerCase() === category.risk_name?.trim().toLowerCase() &&
                            row.category?.trim().toLowerCase() === category.category_name?.trim().toLowerCase()
                    )

                    if (existingRow) {
                        return {
                            id: existingRow.id ? `persisted-${existingRow.id}` : generateRowId(),
                            persistedId: existingRow.id ?? null,
                            risk: category.risk_name,
                            category: category.category_name,
                            pd: existingRow.pd_score ?? "",
                            bi: existingRow.bi_score ?? "",
                            pdBi: existingRow.pd_bi ?? "",
                            consequence: existingRow.consequence ?? "",
                            likelihood: existingRow.likelihood ?? "",
                            inherentRisk: existingRow.inherent_risk ?? "",
                            score: existingRow.inherent_risk_score ?? "",
                        }
                    } else {
                        return createEmptyRow({
                            risk: category.risk_name,
                            category: category.category_name,
                        })
                    }
                })

                const categoryControlRows = sortedCategories.map((category, index) => {
                    const existingRow = serverRows.find(
                        (row) =>
                            row.risk?.trim().toLowerCase() === category.risk_name?.trim().toLowerCase() &&
                            row.category?.trim().toLowerCase() === category.category_name?.trim().toLowerCase()
                    )
                    const riskRow = categoryRows[index]

                    if (existingRow) {
                        return createEmptyControlRow({
                            id: riskRow.id,
                            persistedId: existingRow.id ?? null,
                            risk: category.risk_name,
                            category: category.category_name,
                            design: existingRow.design ?? "",
                            implementation: existingRow.implementation ?? "",
                            controlEfficacy: existingRow.control_efficacy ?? "",
                        })
                    } else {
                        return createEmptyControlRow({
                            id: riskRow.id,
                            persistedId: riskRow.persistedId,
                            risk: category.risk_name,
                            category: category.category_name,
                        })
                    }
                })

                setRiskRows(categoryRows)
                setControlRows(categoryControlRows)

                // Hydrate Loss Scenarios from reports_loss
                setLossRows(mapServerLossRowsToState(lossServerRows))

                // Hydrate Recommendations from reports_recommendations
                if (Array.isArray(recommendationServerRows) && recommendationServerRows.length > 0) {
                    setRecommendationRows(
                        recommendationServerRows.map((row) => {
                            const p = row.priority_values || {}
                            return {
                                id: `rec-${row.id}`,
                                label: row.section || "",
                                low: p.low ?? "",
                                medium: p.medium ?? "",
                                high: p.high ?? "",
                                mandatory: p.mandatory ?? "",
                            }
                        })
                    )
                } else {
                    const sectionNames = getUniqueSectionNamesFromSectionRiskRows(categoryRows)
                    setRecommendationRows(createInitialRecommendationRows(sectionNames))
                }
            } catch (error) {
                console.error("Failed to load report values", error)
                toast.error("Failed to load existing report values.")
            } finally {
                setIsLoadingValues(false)
            }
        }

        fetchReportValues()
    }, [reportId])

    useEffect(() => {
        const fetchReportName = async () => {
            if (!reportId) return
            try {
                const response = await api.get(`/reports/${reportId}`)
                const reportData = response.data?.data || response.data
                setReportName(reportData?.name || "")
            } catch (error) {
                console.error("Failed to load report name", error)
            }
        }

        fetchReportName()
    }, [reportId])


    const syncRiskCategoryAcrossTabs = (rowId, updates, source = "external") => {
        // Risk and category are read-only, so we don't sync changes to them
        // Only sync other fields if needed
        const filteredUpdates = { ...updates }
        delete filteredUpdates.risk
        delete filteredUpdates.category

        if (Object.keys(filteredUpdates).length > 0) {
            setRiskRows((prev) =>
                prev.map((row) => (row.id === rowId ? { ...row, ...filteredUpdates } : row))
            )
            setControlRows((prev) =>
                prev.map((row) => (row.id === rowId ? { ...row, ...filteredUpdates } : row))
            )
            setSectionRows((prev) =>
                prev.map((row) => (row.id === rowId ? { ...row, ...filteredUpdates } : row))
            )
        }
    }

    const syncInherentValues = (rowId, derivedValues) => {
        setSectionRows((prev) =>
            prev.map((row) => {
                if (row.id !== rowId) return row
                const updated = {
                    ...row,
                    inherentRiskLabel: derivedValues.inherentRisk ?? row.inherentRiskLabel,
                    inherentRiskScore: derivedValues.score ?? row.inherentRiskScore,
                }
                updated.riskScore = calculateSectionRiskScore(
                    updated.controlEfficacy,
                    updated.inherentRiskScore
                )
                return updated
            })
        )
    }

    const syncControlEfficacyToSection = (rowId, controlEfficacyValue) => {
        setSectionRows((prev) =>
            prev.map((row) => {
                if (row.id !== rowId) return row
                const updated = { ...row, controlEfficacy: controlEfficacyValue || "" }
                updated.riskScore = calculateSectionRiskScore(
                    updated.controlEfficacy,
                    updated.inherentRiskScore
                )
                return updated
            })
        )
    }

    const inherentRiskTabRef = useRef(null)
    const controlEfficacyTabRef = useRef(null)


    const handleUpdate = async ({ lossRows = [], recommendationRows = [] } = {}) => {
        if (!reportId) {
            toast.error("Missing report reference.")
            return
        }

        const controlMap = controlRows.reduce((acc, row) => {
            acc[row.id] = row
            return acc
        }, {})

        const sectionMap = sectionRows.reduce((acc, row) => {
            acc[row.id] = row
            return acc
        }, {})

        const combinedRows = []
        riskRows.forEach((riskRow) => {
            const riskName = (riskRow.risk || "").trim()
            const categoryName = (riskRow.category || "").trim()
            if (!riskName || !categoryName) {
                return
            }

            const controlRow = controlMap[riskRow.id] || {}
            const sectionRow = sectionMap[riskRow.id] || {}

            combinedRows.push({
                id: riskRow.persistedId ?? null,
                risk: riskRow.risk,
                category: riskRow.category,
                pdScore: riskRow.pd,
                biScore: riskRow.bi,
                pdBi: riskRow.pdBi,
                consequence: riskRow.consequence,
                likelihood: riskRow.likelihood,
                inherentRisk: riskRow.inherentRisk,
                inherentRiskScore: riskRow.score,
                design: controlRow.design ?? "",
                implementation: controlRow.implementation ?? "",
                controlEfficacy: controlRow.controlEfficacy ?? "",
                sectionRiskScore: sectionRow.riskScore ?? "",
            })
        })

        setIsSaving(true)
        try {
            const response = await api.put(`/reports/${reportId}/values`, {
                rows: combinedRows,
                deletedIds: [], // No deletions allowed for risk rows
                lossRows,
                recommendationRows,
                deletedLossIds,
            })
            toast.success("Report values saved successfully.")
            setLossRows(mapServerLossRowsToState(response.data?.loss))
            setDeletedLossIds([])
        } catch (error) {
            console.error("Failed to save report values", error)
            const message = error.response?.data?.message || "Failed to save report values."
            toast.error(message)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <ReportValuesLayout
            reportName={reportName}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onUpdate={handleUpdate}
            isSaving={isSaving}
            isLoadingValues={isLoadingValues}
            backLink="/reports"
            inherentRiskTabRef={inherentRiskTabRef}
            inherentRiskTabProps={{
                rows: riskRows,
                setRows: setRiskRows,
                onRiskCategoryChange: (rowId, updates) =>
                    syncRiskCategoryAcrossTabs(rowId, updates, "inherent"),
                onDerivedValuesChange: syncInherentValues,
            }}
            controlEfficacyTabRef={controlEfficacyTabRef}
            controlEfficacyTabProps={{
                rows: controlRows,
                setRows: setControlRows,
                onControlEfficacyChange: syncControlEfficacyToSection,
                onRiskCategoryChange: (rowId, updates) =>
                    syncRiskCategoryAcrossTabs(rowId, updates, "control"),
            }}
            sectionRiskScoreTabProps={{
                rows: sectionRows,
                onRiskCategoryChange: (rowId, updates) =>
                    syncRiskCategoryAcrossTabs(rowId, updates, "section"),
            }}
            lossRows={lossRows}
            setLossRows={setLossRows}
            recommendationRows={recommendationRows}
            setRecommendationRows={setRecommendationRows}
            onLossRowRemoved={(persistedId) =>
                setDeletedLossIds((prev) =>
                    prev.includes(persistedId) ? prev : [...prev, persistedId]
                )
            }
        />
    )
}

export default EditValuesPage;